import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Paper } from 'js/social/shared/types';
import {
  createBookmark,
  retrieveDownloadUrl,
} from 'js/social/api/work_downloader_api';
import SnackBar from 'js/social/shared/SnackBar';

export interface Props {
  work: Paper;
  sourceForBookmarking?: string;
  newsFeedLoadedRef?: React.MutableRefObject<PromiseLike<boolean>>;
  onComplete?: () => void;
}

const AttachmentAutoDownloader = ({
  work,
  sourceForBookmarking,
  newsFeedLoadedRef,
  onComplete = () => null,
}: Props) => {
  const [downloadUrl, setDownloadUrl] = useState<string>();
  const [downloadComplete, setDownloadComplete] = useState<boolean>(false);
  const [error, setError] = useState<string>();

  const autoDownloader = useRef<HTMLAnchorElement>(null);

  const findDownloadUrl = useCallback(async () => {
    if (work) {
      try {
        setDownloadUrl(await retrieveDownloadUrl(work.id));
      } catch (e) {
        setError((e as Error).message);
      }
    }
  }, [work]);

  const handleBookmarking = useCallback(async () => {
    if (work && sourceForBookmarking) {
      await createBookmark(work.id, sourceForBookmarking);
    }
  }, [work, sourceForBookmarking]);

  useEffect(() => {
    findDownloadUrl();
  }, [work, findDownloadUrl]);

  useEffect(() => {
    if (downloadUrl) {
      Promise.all([handleBookmarking(), newsFeedLoadedRef?.current]).then(
        () => {
          autoDownloader.current?.click();

          const url = new URL(window.location.toString());
          url.searchParams.delete('home_download');
          window.history.replaceState(null, '', url);

          setDownloadComplete(true);
          onComplete();
        },
      );
    }
  }, [downloadUrl, handleBookmarking, newsFeedLoadedRef, onComplete]);

  return (
    <div>
      {work && work.attachment && !downloadComplete && (
        <a
          href={downloadUrl}
          target="" // Open in current tab to circumvent popup blockers
          ref={autoDownloader}
          download=""
          style={{ display: 'none' }}
        ></a>
      )}
      {error && (
        <SnackBar
          type={'failure'}
          message={'Sorry, your PDF download failed'}
        />
      )}
    </div>
  );
};

export default AttachmentAutoDownloader;
